/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale Hightouch.Spelling = NO -->*/
/*<!-- vale Hightouch.Spelling = YES -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Create models with the SQL editor", description = "Create Hightouch models using the SQL editor.", lastUpdatedDate = new Date(1696377600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Create models with the SQL editor",
        description: "Create Hightouch models using the SQL editor.",
        lastUpdatedDate: "Wed Oct 04 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Unique primary key requirement",
          "depth": 0,
          "id": "unique-primary-key-requirement"
        }, {
          "value": "Filter out duplicates",
          "depth": 1,
          "id": "filter-out-duplicates"
        }, {
          "value": "Composite primary keys",
          "depth": 1,
          "id": "composite-primary-keys"
        }, {
          "value": "Save a model without results",
          "depth": 1,
          "id": "save-a-model-without-results"
        }, {
          "value": "Exclude rows with null values",
          "depth": 1,
          "id": "exclude-rows-with-null-values"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      code: "code",
      pre: "pre",
      span: "span",
      strong: "strong",
      img: "img",
      a: "a",
      h2: "h2",
      ul: "ul",
      li: "li",
      h3: "h3",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "With the SQL editor modeling method, you can write a SQL query to define your model. The SQL editor can execute whatever SQL is native to your warehouse or database."
      }), "\n", _jsxs(_components.p, {
        children: ["Your query depends on the data in your model. For example, if you are using the Sample B2B SaaS Source, you can use the following query to select the entire ", _jsx(_components.code, {
          children: "public.users"
        }), " table:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " public.users\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Before you can continue, you must click ", _jsx(_components.strong, {
          children: "Preview"
        }), " to see a preview of the data. By default, Hightouch limits the preview to the first 100 rows. Once you've confirmed that the preview contains the data you expect, click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "models/create-model-preview-query.png",
          alt: "Model preview using the SQL editor"
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["You can't save a model if your query doesn't return any results. If you need to save a model with such a query because you expect there to be results in the future, see the ", _jsx(_components.a, {
            href: "#save-a-model-without-results",
            children: "save a model without results section"
          }), "."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The last step for model setup is to enter a descriptive ", _jsx(_components.strong, {
          children: "Name"
        }), " and select the column to use as a ", _jsx(_components.a, {
          href: "/getting-started/concepts#unique-primary-key-requirement",
          children: _jsx(_components.strong, {
            children: "Primary key"
          })
        }), ". You can optionally select a ", _jsx(_components.a, {
          href: "/workspace-management/folders-filters#folders",
          children: "folder"
        }), " to move the model to."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "models/create-model-primary-key.png",
          alt: "Setting a model Name and Primary key in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Click ", _jsx(_components.strong, {
          children: "Finish"
        }), " to complete your model setup."]
      }), "\n", _jsx(_components.h2, {
        id: "unique-primary-key-requirement",
        children: "Unique primary key requirement"
      }), "\n", _jsxs(_components.p, {
        children: ["All models require a ", _jsxs(_components.a, {
          href: "/models/creating-models#unique-primary-key-requirement",
          children: ["unique ", _jsx(_components.strong, {
            children: "primary key"
          })]
        }), ".\nIf your dataset doesn't inherently include any truly unique columns, you can use SQL to either:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#filter-out-duplicates",
            children: "filter out"
          }), " duplicate rows, if you're not concerned about losing data from one or more of the duplicated rows"]
        }), "\n", _jsxs(_components.li, {
          children: ["create a ", _jsx(_components.a, {
            href: "#composite-primary-keys",
            children: "composite column"
          }), " to use for your primary key"]
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["As explained in the ", _jsx(_components.a, {
            href: "/models/creating-models#updating-a-primary-key",
            children: "primary key updates"
          }), " section, if you alter a model's primary key by selecting a different column, you will be prompted to perform a full rsync or reset the change data capture for all syncs that depend on that model. Be careful when modifying primary keys. If you keep the same column name but alter the way its values are calculated, some records may be added or deleted in your destination, depending on how your sync is configured."]
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Hightouch intentionally stringifies your chosen ", _jsx(_components.a, {
            href: "/getting-started/concepts#unique-primary-key-requirement",
            children: _jsx(_components.strong, {
              children: "primary key"
            })
          }), " column for enhanced performance during ", _jsx(_components.a, {
            href: "/getting-started/concepts#change-data-capture",
            children: "change data capture"
          }), ".\nIf you need to sync the primary key column as a non-string value, use ", _jsx(_components.a, {
            href: "/models/data-types-casting#sql-aliasing",
            children: "SQL aliasing"
          }), " in your model to create a new column specifically for syncing."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "filter-out-duplicates",
        children: "Filter out duplicates"
      }), "\n", _jsxs(_components.p, {
        children: ["When you write a ", _jsx(_components.code, {
          children: "SELECT"
        }), " statement to define your model, you can use a ", _jsx(_components.code, {
          children: "GROUP BY"
        }), " clause to retrieve only the unique rows based on the column you want to use for your primary key:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-SQL",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " users\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GROUP"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "BY"
          }), " primary_key_column\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This query returns only one row for each unique ", _jsx(_components.code, {
          children: "primary_key_column"
        }), " value, while ignoring the duplicates."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["This query returns one row for each ", _jsx(_components.code, {
            children: "primary_key_column"
          }), " value, so if there are multiple rows with the same ", _jsx(_components.code, {
            children: "primary_key_column"
          }), " value, only one of them will be returned.\nIf you want your model's query results to return every row in your dataset, it's better to create a ", _jsx(_components.a, {
            href: "#composite-primary-keys",
            children: "composite primary key"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "composite-primary-keys",
        children: "Composite primary keys"
      }), "\n", _jsx(_components.p, {
        children: "If your dataset doesn't inherently include a truly unique primary key, you can create one. For example, in a CRM, you may have users that are identified by email, but belong to multiple organizations:"
      }), "\n", "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "org_id"
            }), _jsx(_components.th, {
              children: "email"
            }), _jsx(_components.th, {
              children: "created_at"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "3"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "mailto:alice@example.com",
                children: "alice@example.com"
              })
            }), _jsx(_components.td, {
              children: "2023-01-18"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "1"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "mailto:bob@example.com",
                children: "bob@example.com"
              })
            }), _jsx(_components.td, {
              children: "2022-05-06"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "3"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "mailto:bob@example.com",
                children: "bob@example.com"
              })
            }), _jsx(_components.td, {
              children: "2022-12-13"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "2"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "mailto:carol@example.com",
                children: "carol@example.com"
              })
            }), _jsx(_components.td, {
              children: "2023-03-29"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["The user ", _jsx(_components.code, {
          children: "bob@example.com"
        }), " belongs to both ", _jsx(_components.code, {
          children: "org_id:1"
        }), " and ", _jsx(_components.code, {
          children: "org_id:3"
        }), ". Their email address is unique within each organization but non-unique across organizations. Therefore, if you were to select ", _jsx(_components.code, {
          children: "email"
        }), " as the primary key, downstream destinations receiving the data would likely reject these rows."]
      }), "\n", _jsxs(_components.p, {
        children: ["To ensure a sync with this type of data includes all records, you can use a ", _jsx(_components.strong, {
          children: "hash function"
        }), " for the model's primary key. The hash should combine enough columns in the data to create a unique value, for example, ", _jsx(_components.code, {
          children: "org_id"
        }), " and ", _jsx(_components.code, {
          children: "email"
        }), "."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-SQL",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " org_id,\n       email,\n       created_at,\n       HASH(CONCAT(org_id, ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'-'"
          }), ", email)) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " composite_key\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " users\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This query selects the ", _jsx(_components.code, {
          children: "org_id"
        }), ", ", _jsx(_components.code, {
          children: "email"
        }), ", and ", _jsx(_components.code, {
          children: "created_at"
        }), " columns, and creates a ", _jsx(_components.code, {
          children: "composite_key"
        }), " column.\nThe composite_key column contains a hash of the concatenation of ", _jsx(_components.code, {
          children: "org_id"
        }), " and ", _jsx(_components.code, {
          children: "email"
        }), ", creating a unique value for each row."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "org_id"
            }), _jsx(_components.th, {
              children: "email"
            }), _jsx(_components.th, {
              children: "created_at"
            }), _jsx(_components.th, {
              children: "composite_key"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "3"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "mailto:alice@example.com",
                children: "alice@example.com"
              })
            }), _jsx(_components.td, {
              children: "2023-01-18"
            }), _jsx(_components.td, {
              children: "123456780"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "1"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "mailto:bob@example.com",
                children: "bob@example.com"
              })
            }), _jsx(_components.td, {
              children: "2022-05-06"
            }), _jsx(_components.td, {
              children: "987654321"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "3"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "mailto:bob@example.com",
                children: "bob@example.com"
              })
            }), _jsx(_components.td, {
              children: "2022-12-13"
            }), _jsx(_components.td, {
              children: "709230458"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "2"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "mailto:carol@example.com",
                children: "carol@example.com"
              })
            }), _jsx(_components.td, {
              children: "2023-03-29"
            }), _jsx(_components.td, {
              children: "382910382"
            })]
          })]
        })]
      }), "\n", "\n", _jsxs(_components.p, {
        children: ["You would then select the ", _jsx(_components.code, {
          children: "composite_key"
        }), " column as the primary key for the model.\nBecause the composite keys for the ", _jsx(_components.code, {
          children: "bob@example.com"
        }), " rows are unique, Hightouch would sync both rows to the destination."]
      }), "\n", _jsx(_components.h3, {
        id: "save-a-model-without-results",
        children: "Save a model without results"
      }), "\n", _jsx(_components.p, {
        children: "You can't save a model if your query doesn't return any results.\nIf you need to save a model with such a query because you expect there to be results in the future, add the following SQL to the end of your query:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "UNION"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ALL"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "select"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'ignore'"
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'ignore'"
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'ignore'"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This SQL adds a row of 'ignore' values that don't match against anything in your destination but are always present so you can save the query.\nIf your column datatype isn't a ", _jsx(_components.code, {
          children: "string"
        }), ", you can replace 'ignore' with a ", _jsx(_components.code, {
          children: "null"
        }), " value.\nThe number of 'ignore' or ", _jsx(_components.code, {
          children: "null"
        }), " values needs to be the same as the number of columns in your model."]
      }), "\n", _jsx(_components.h3, {
        id: "exclude-rows-with-null-values",
        children: "Exclude rows with null values"
      }), "\n", _jsxs(_components.p, {
        children: ["Some ", _jsx(_components.a, {
          href: "/getting-started/concepts#destinations",
          children: "destinations"
        }), " reject requests when required fields contain ", _jsx(_components.code, {
          children: "null"
        }), " values.\nYou can prevent syncs from sending ", _jsx(_components.code, {
          children: "null"
        }), " values using the ", _jsxs(_components.a, {
          href: "/syncs/mapping-data#dont-sync-null-values",
          children: [_jsx(_components.strong, {
            children: "Don't sync null values"
          }), " option"]
        }), " in the ", _jsx(_components.a, {
          href: "/syncs/mapping-data#advanced-mapper",
          children: "advanced mapper"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you want to exclude rows with ", _jsx(_components.code, {
          children: "null"
        }), " values at a model level, you can include the following SQL in your model defintiion:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " your_table\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " column1 ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IS"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NULL"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AND"
          }), " column2 ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IS"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NULL"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AND"
          }), " column3 ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IS"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NULL"
          }), ";\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Be sure to change ", _jsx(_components.code, {
          children: "column1"
        }), ", ", _jsx(_components.code, {
          children: "column2"
        }), ", etc. to your column names and to include any columns where you believe there could be ", _jsx(_components.code, {
          children: "null"
        }), " values."]
      })]
    });
  }
}
export default MDXContent;
